import type { Receipt } from '@arcnet/contracts';
import { useReceipt } from '../../lib/api/hooks-payments.js';
import { ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { formatMoney, formatDateTime } from '../../lib/format.js';
import { docDate, printDocument } from './InvoiceDocument.js';

/**
 * A payment's receipt, from the same facts as the email the client was sent:
 * airtime bought by card, or a payment against invoices recorded by hand.
 */

const METHOD: Record<Receipt['method'], string> = {
  card: 'Card, through PayFast', eft: 'EFT', cash: 'Cash', other: 'Other',
};

export function ReceiptView({ tenantId, receiptNumber, onBack }: {
  tenantId: string | null;
  receiptNumber: string;
  onBack: () => void;
}) {
  const receipt = useReceipt(tenantId, receiptNumber);

  return (
    <>
      <div className="arc-row" style={{ gap: 8, marginBottom: 12 }}>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={onBack}>
          <Icon name="arrowLeft" size={13} /> Payments
        </button>
        <div className="arc-spacer" />
        {receipt.data && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={printDocument}>Print</button>
        )}
      </div>
      {receipt.isLoading
        ? <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 240 }} /></div>
        : receipt.error
          ? <ErrorBanner error={receipt.error} />
          : receipt.data ? <ReceiptCard receipt={receipt.data} /> : null}
    </>
  );
}

function ReceiptCard({ receipt: r }: { receipt: Receipt }) {
  const i = r.issuer;
  return (
    <div className="arc-card arc-printable">
      <div className="arc-card-body">
        <div className="arc-row" style={{ alignItems: 'flex-start', gap: 16 }}>
          <div>
            <div className="arc-subtle" style={{ fontSize: 12.5 }}>{i.name}</div>
            <h2 className="arc-h2" style={{ marginTop: 2 }}>Payment receipt</h2>
          </div>
          <div className="arc-spacer" />
          <div style={{ textAlign: 'right' }}>
            <div className="arc-mono" style={{ fontWeight: 600 }}>{r.receipt_number}</div>
            {r.mode === 'sandbox' && <Badge tone="info">test payment</Badge>}
          </div>
        </div>

        <dl className="arc-kv" style={{ marginTop: 18 }}>
          <dt>Date</dt>
          <dd>{r.received_on ? docDate(r.received_on) : formatDateTime(r.paid_at, 'Africa/Johannesburg')}</dd>
          <dt>Account</dt><dd>{r.account_name}</dd>
          <dt>For</dt><dd>{r.purpose === 'invoice' ? `Invoice ${r.invoices.join(', ')}` : 'Airtime'}</dd>
          <dt>Amount</dt><dd style={{ fontWeight: 600 }}>{formatMoney(r.amount, r.currency)}</dd>
          <dt>Paid by</dt><dd>{METHOD[r.method]}</dd>
          {r.provider_payment_id && (
            <><dt>PayFast reference</dt><dd className="arc-mono">{r.provider_payment_id}</dd></>
          )}
          {r.reference && <><dt>Reference</dt><dd>{r.reference}</dd></>}
        </dl>

        <div className="arc-receipt-issuer">
          <div>{i.registered_name}</div>
          {i.registration_number && <div>Registration {i.registration_number}</div>}
          {i.vat_number && <div>VAT {i.vat_number}</div>}
          {i.address?.split('\n').map((line, n) => <div key={n}>{line}</div>)}
          {(i.email || i.phone) && <div>{[i.email, i.phone].filter(Boolean).join(', ')}</div>}
          <div style={{ marginTop: 8 }}>This is a receipt for a payment, not a tax invoice.</div>
        </div>
      </div>
    </div>
  );
}
